import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
    GetProductsResponse,
    GetCartResponse,
    CartData,
    UpdateCartItemRequest,
    GetCouponsResponse,
    ApplyCouponRequest,
    ApplyCouponResponse,
    CartCheckoutResponse,
    ProductType,
    GetCreditStatusResponse,
} from '@/types/cart.types';


export interface VerifyPaymentRequest {
    razorpay_order_id: string;
    razorpay_payment_id: string;
    razorpay_signature: string;
    transactionId: string;
}

export interface VerifyPaymentResponse {
    success: boolean;
    message: string;
    transactionId: string;
    purchaseType: string;
    status: string;
}


export const cartApi = createApi({
    reducerPath: 'cartApi',
    baseQuery: fetchBaseQuery({
        baseUrl: `${process.env.NEXT_PUBLIC_API_URL}`,
        prepareHeaders: (headers, { getState }) => {
            const token =
                (getState() as any).auth.token ||
                (typeof window !== 'undefined' ? localStorage.getItem('token') : null);
            if (token) {
                headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
        },
    }),
    tagTypes: ['Cart', 'Coupons'],
    endpoints: (builder) => ({
        // ─── Public ─────────────────────────────────────────────
        getProducts: builder.query<GetProductsResponse, string | void>({
            query: (currency) => ({
                url: '/cart/products',
                params: currency ? { currency } : undefined,
            }),
        }),

        // ─── Cart CRUD (Auth required) ───────────────────────────
        getCart: builder.query<GetCartResponse, string | void>({
            query: (currency) => ({
                url: '/cart',
                params: currency ? { currency } : undefined,
            }),
            providesTags: ['Cart'],
        }),

        updateCartItem: builder.mutation<GetCartResponse, UpdateCartItemRequest & { currency?: string }>({
            query: (body) => ({
                url: '/cart/item',
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['Cart', 'Coupons'],
        }),

        removeCartItem: builder.mutation<GetCartResponse, ProductType>({
            query: (productType) => ({
                url: `/cart/item/${productType}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Cart', 'Coupons'],
        }),

        clearCart: builder.mutation<GetCartResponse, void>({
            query: () => ({
                url: '/cart',
                method: 'DELETE',
            }),
            invalidatesTags: ['Cart', 'Coupons'],
        }),

        // ─── Coupons ────────────────────────────────────────────
        getCoupons: builder.query<GetCouponsResponse, string | void>({
            query: (currency) => ({
                url: '/cart/coupons',
                params: currency ? { currency } : undefined,
            }),
            providesTags: ['Coupons'],
        }),

        applyCoupon: builder.mutation<ApplyCouponResponse, ApplyCouponRequest>({
            query: (body) => ({
                url: '/cart/coupon',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Cart', 'Coupons'],
        }),

        removeCoupon: builder.mutation<GetCartResponse, void>({
            query: () => ({
                url: '/cart/coupon',
                method: 'DELETE',
            }),
            invalidatesTags: ['Cart', 'Coupons'],
        }),

        // ─── Checkout ────────────────────────────────────────────
        checkout: builder.mutation<CartCheckoutResponse, string | void>({
            query: (currency) => ({
                url: '/cart/checkout',
                method: 'POST',
                params: currency ? { currency } : undefined,
            }),
        }),
        // ─── Payment Verification ─────────────────────────────────
        verifyCartPayment: builder.mutation<VerifyPaymentResponse, VerifyPaymentRequest>({
            query: (body) => ({
                url: '/razorpay/verify',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Cart'],
        }),
        // ─── Credit Status (new CART plan shape) ──────────────────
        getCartCreditStatus: builder.query<GetCreditStatusResponse, string | void>({
            query: (currency) => ({
                url: '/pricing/status',
                params: currency ? { currency } : undefined,
            }),
            providesTags: ['Cart'],
        }),
    }),
});


export const {
    useGetProductsQuery,
    useGetCartQuery,
    useUpdateCartItemMutation,
    useRemoveCartItemMutation,
    useClearCartMutation,
    useGetCouponsQuery,
    useApplyCouponMutation,
    useRemoveCouponMutation,
    useCheckoutMutation,
    useVerifyCartPaymentMutation,
    useGetCartCreditStatusQuery,
} = cartApi;


